import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { DayPicker, type DateRange } from "react-day-picker";
import { enUS } from "date-fns/locale";
import { format } from "date-fns";
import CalendarMonth from "../../components/Atomos/Icons/CalendarMonth";
import Button from "../../components/staff/Button";
import { useStaffMode } from "../../hooks/StaffModeProvider";
import type { TemplateDatePreset } from "./types";
import type { TemplateDateFilterValue } from "../../utils/templateDateFilterTypes";
import {
  buildFirmLogsDateFilterValue,
  clearFirmLogsDateField,
  type FirmLogsDateField,
} from "../../utils/auditLogDateFilters";
import { getPresetLocalDateRange } from "./templateDateFilter";
import "../../assets/css/Calendar.css";
import "./TemplateDateFlightsPicker.css";
import "../../assets/css/InputText.css";

const QUICK_PRESETS: { id: TemplateDatePreset; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "last-week", label: "Last week" },
  { id: "last-month", label: "Last month" },
  { id: "all", label: "Clear" },
];

/** Default portal layer; use {@link TEMPLATE_DATE_PICKER_NESTED_MODAL_Z_INDEX} inside Modal. */
export const TEMPLATE_DATE_PICKER_DEFAULT_Z_INDEX = 220;
/** Above shared `Modal` overlay (`z-[1000]`). */
export const TEMPLATE_DATE_PICKER_NESTED_MODAL_Z_INDEX = 1100;
export const CATALOG_DATE_PICKER_LABEL_CLASS = "font-semibold text-gray-font";
export const CATALOG_DATE_PICKER_TRIGGER_CLASS =
  "rounded-md border border-solid !border-gray-300 !bg-gray-25 !shadow-none !hover:shadow-md";
export const CATALOG_DATE_PICKER_INSET_CLASS = "max-h-[0.875rem]";

function buildYearPresets(): { id: TemplateDatePreset; label: string }[] {
  const currentYear = new Date().getFullYear();
  return [
    { id: "current-year", label: String(currentYear) },
    { id: "year-2025", label: "2025" },
    { id: "year-2024", label: "2024" },
    { id: "year-2023", label: "2023" },
    { id: "year-2022", label: "2022" },
  ];
}

function parseLocalYmd(s: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((s ?? "").trim());
  if (!m) {
    return undefined;
  }
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return undefined;
  }
  return dt;
}

function toYyyyMmDd(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function formatDisplay(d: Date): string {
  return format(d, "MMM d, yyyy", { locale: enUS });
}

function formatDisplayDmy(d: Date): string {
  return format(d, "dd/MM/yyyy");
}

function initDraftFromValue(value: TemplateDateFilterValue): {
  range: DateRange | undefined;
  applyMode: TemplateDatePreset;
} {
  const preset = value.templateDatePreset;
  if (preset === "all") {
    return { range: undefined, applyMode: "all" };
  }
  if (preset !== "custom") {
    const r = getPresetLocalDateRange(
      preset as Exclude<TemplateDatePreset, "custom" | "all">,
    );
    return { range: { from: r.from, to: r.to }, applyMode: preset };
  }
  const f = parseLocalYmd(value.templateDateFrom);
  const t = parseLocalYmd(value.templateDateTo);
  if (f && t) {
    return { range: { from: f, to: t }, applyMode: "custom" };
  }
  if (f) {
    return { range: { from: f, to: f }, applyMode: "custom" };
  }
  return { range: undefined, applyMode: "custom" };
}

function getTriggerLabels(value: TemplateDateFilterValue): {
  from: string;
  to: string;
} {
  if (value.templateDatePreset === "all") {
    return { from: "", to: "" };
  }
  if (value.templateDatePreset !== "custom") {
    const r = getPresetLocalDateRange(
      value.templateDatePreset as Exclude<
        TemplateDatePreset,
        "custom" | "all"
      >,
    );
    return { from: formatDisplay(r.from), to: formatDisplay(r.to) };
  }
  const f = parseLocalYmd(value.templateDateFrom);
  const t = parseLocalYmd(value.templateDateTo);
  if (f && t) {
    return { from: formatDisplay(f), to: formatDisplay(t) };
  }
  if (f) {
    return { from: formatDisplay(f), to: formatDisplay(f) };
  }
  return { from: "", to: "" };
}

function getFirmLogsTriggerLabels(value: TemplateDateFilterValue): {
  from: string;
  to: string;
} {
  if (value.templateDatePreset === "all") {
    return { from: "", to: "" };
  }
  if (value.templateDatePreset !== "custom") {
    const r = getPresetLocalDateRange(
      value.templateDatePreset as Exclude<
        TemplateDatePreset,
        "custom" | "all"
      >,
    );
    return { from: formatDisplayDmy(r.from), to: formatDisplayDmy(r.to) };
  }
  const f = parseLocalYmd(value.templateDateFrom);
  const t = parseLocalYmd(value.templateDateTo);
  return {
    from: f ? formatDisplayDmy(f) : "",
    to: t ? formatDisplayDmy(t) : "",
  };
}

const FIRM_LOGS_DATE_TRIGGER_CLASS =
  "flex min-h-[42px] min-w-[10.5rem] items-center justify-between gap-2 rounded-md bg-gray-25 px-3 py-[11px] text-sm outline-none transition-shadow hover:shadow-sm focus-visible:ring-2 focus-visible:ring-Default focus-visible:ring-offset-1";

export type TemplateDateFlightsPickerProps = {
  value: TemplateDateFilterValue;
  onApply: (next: TemplateDateFilterValue) => void;
  /** Full Send Email card vs compact catalog filter row */
  variant?: "default" | "catalog" | "firmLogs";
  /** Catalog row: same style as other “By …” filters */
  label?: string;
  dialogTitle?: string;
  labelClassName?: string;
  triggerClassName?: string;
  triggerInsetClassName?: string;
  /** Year shortcut row (current year, 2025, 2024, …). Default true. */
  showYearPresets?: boolean;
  /** Adds a rolling last-3-days shortcut next to Today. Default false. */
  showLast3Days?: boolean;
  /** Adds a rolling last-7-days shortcut next to Last 3 days. Default false. */
  showLast7Days?: boolean;
  /** Calendar-week shortcut (previous Monday–Sunday). Default true. */
  showLastWeek?: boolean;
  /** Portal overlay z-index (default {@link TEMPLATE_DATE_PICKER_DEFAULT_Z_INDEX}). */
  portalZIndex?: number;
  /** Hides the surface label (e.g. inline toolbar in firm logs modal). */
  hideLabel?: boolean;
};

/**
 * Google Flights–style range control: dual-month calendar, quick presets and Done
 * in the footer; English month names via react-day-picker + date-fns enUS.
 */
export default function TemplateDateFlightsPicker({
  value,
  onApply,
  variant = "default",
  label,
  dialogTitle,
  labelClassName,
  triggerClassName,
  triggerInsetClassName,
  showYearPresets = true,
  showLast3Days = false,
  showLast7Days = false,
  showLastWeek = true,
  portalZIndex = TEMPLATE_DATE_PICKER_DEFAULT_Z_INDEX,
  hideLabel = false,
}: TemplateDateFlightsPickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [firmLogsOpenField, setFirmLogsOpenField] =
    useState<FirmLogsDateField | null>(null);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>();
  const [applyMode, setApplyMode] = useState<TemplateDatePreset>("today");
  const [month, setMonth] = useState<Date>(() => new Date());
  const { isStaffMode } = useStaffMode();
  const colorClass = isStaffMode ? "staff-mode-calendar" : "";
  const isCatalog = variant === "catalog";
  const isFirmLogs = variant === "firmLogs";

  const triggerButtonClass = useMemo(() => {
    const trimmedCustom = triggerClassName?.trim() ?? "";
    const shellBase = [
      "flex w-full items-stretch overflow-hidden text-left outline-none transition-colors transition-shadow duration-200",
    ]
      .filter(Boolean)
      .join(" ");

    const focusRing = isStaffMode
      ? "hover:border-secondary focus-visible:border-secondary focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      : "hover:border-primary focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white";

    if (trimmedCustom !== "") {
      return `${shellBase} ${focusRing} ${trimmedCustom}`.trim();
    }

    if (isCatalog) {
      return `${shellBase} ${focusRing} ${CATALOG_DATE_PICKER_TRIGGER_CLASS}`.trim();
    }

    return [
      shellBase,
      "rounded-md border-2 border-solid border-gray-300 bg-white shadow-sm hover:shadow-md",
      focusRing,
    ]
      .filter(Boolean)
      .join(" ");
  }, [isStaffMode, triggerClassName, isCatalog]);

  const labelFinalClass =
    labelClassName != null && labelClassName.trim() !== ""
      ? labelClassName.trim()
      : isCatalog
        ? CATALOG_DATE_PICKER_LABEL_CLASS
        : "text-base font-semibold leading-none text-gray-font md:text-lg";

  const insetGridClass =
    [
      "grid flex-1 grid-cols-1 divide-y divide-solid divide-gray-300 bg-white text-sm md:grid-cols-2 md:divide-x md:divide-y-0",
      isCatalog ? "min-h-[2.5rem] md:min-h-[2.75rem]" : "min-h-[2.75rem] md:min-h-[3rem] md:text-base",
      triggerInsetClassName?.trim() ||
        (isCatalog ? CATALOG_DATE_PICKER_INSET_CLASS : ""),
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

  const triggerIconRailClass = useMemo(
    () =>
      [
        "flex shrink-0 items-center border-r border-solid px-2.5 sm:px-3",
        isStaffMode
          ? "border-gray-300 bg-secondary/[0.08] text-secondary hover:border-secondary/40"
          : "border-gray-300 bg-primary/[0.08] text-primary hover:border-primary/40",
      ].join(" "),
    [isStaffMode],
  );

  const dialogPanelClass =
    "relative z-[1] mt-2 w-[calc(100vw-1.5rem)] max-w-[56rem] rounded-xl bg-white p-4 shadow-2xl sm:w-full sm:p-5 md:p-6";

  const triggerLabels = useMemo(
    () =>
      isFirmLogs ? getFirmLogsTriggerLabels(value) : getTriggerLabels(value),
    [isFirmLogs, value],
  );
  const yearPresets = useMemo(() => buildYearPresets(), []);
  const quickPresets = useMemo(() => {
    const base = showLastWeek
      ? QUICK_PRESETS
      : QUICK_PRESETS.filter((preset) => preset.id !== "last-week");
    const rolling: { id: TemplateDatePreset; label: string }[] = [];
    if (showLast3Days) {
      rolling.push({ id: "last-3-days", label: "Last 3 days" });
    }
    if (showLast7Days) {
      rolling.push({ id: "last-7-days", label: "Last 7 days" });
    }
    if (rolling.length === 0) {
      return base;
    }
    return [base[0], ...rolling, ...base.slice(1)];
  }, [showLast3Days, showLast7Days, showLastWeek]);
  const surfaceLabel = label ?? "Template Date";
  const modalHeading = dialogTitle ?? surfaceLabel;

  const openPicker = useCallback(() => {
    const { range, applyMode: mode } = initDraftFromValue(value);
    setDraftRange(range);
    setApplyMode(mode);
    setMonth(range?.from ?? range?.to ?? new Date());
    setOpen(true);
  }, [value]);

  const closePicker = useCallback(() => {
    setOpen(false);
  }, []);

  const closeFirmLogsPicker = useCallback(() => {
    setFirmLogsOpenField(null);
  }, []);

  const openFirmLogsPicker = useCallback(
    (field: FirmLogsDateField) => {
      const existing =
        field === "from"
          ? parseLocalYmd(value.templateDateFrom)
          : parseLocalYmd(value.templateDateTo);
      setMonth(existing ?? new Date());
      setFirmLogsOpenField(field);
    },
    [value.templateDateFrom, value.templateDateTo],
  );

  const applyFirmLogsDate = useCallback(
    (field: FirmLogsDateField, date: Date) => {
      onApply(buildFirmLogsDateFilterValue(field, date, value));
      setFirmLogsOpenField(null);
    },
    [onApply, value],
  );

  const firmLogsBoundFrom = useMemo(
    () => parseLocalYmd(value.templateDateFrom),
    [value.templateDateFrom],
  );
  const firmLogsBoundTo = useMemo(
    () => parseLocalYmd(value.templateDateTo),
    [value.templateDateTo],
  );

  const firmLogsSingleDisabled = useMemo(() => {
    if (firmLogsOpenField === "from" && firmLogsBoundTo) {
      return { after: firmLogsBoundTo };
    }
    if (firmLogsOpenField === "to" && firmLogsBoundFrom) {
      return { before: firmLogsBoundFrom };
    }
    return undefined;
  }, [firmLogsBoundFrom, firmLogsBoundTo, firmLogsOpenField]);

  const firmLogsSelectedDate = useMemo(() => {
    if (firmLogsOpenField === "from") {
      return firmLogsBoundFrom;
    }
    if (firmLogsOpenField === "to") {
      return firmLogsBoundTo;
    }
    return undefined;
  }, [firmLogsBoundFrom, firmLogsBoundTo, firmLogsOpenField]);

  const handlePresetInPopover = (id: TemplateDatePreset) => {
    setApplyMode(id);
    if (id === "all") {
      setDraftRange(undefined);
      return;
    }
    const r = getPresetLocalDateRange(
      id as Exclude<TemplateDatePreset, "custom" | "all">,
    );
    setDraftRange({ from: r.from, to: r.to });
    setMonth(r.from);
  };

  const handleDone = () => {
    if (applyMode !== "custom") {
      onApply({
        templateDatePreset: applyMode,
        templateDateFrom: "",
        templateDateTo: "",
      });
    } else if (!draftRange?.from) {
      onApply({
        templateDatePreset: "all",
        templateDateFrom: "",
        templateDateTo: "",
      });
    } else {
      const fromD = startOfLocalDay(draftRange.from);
      const toD = startOfLocalDay(draftRange.to ?? draftRange.from);
      onApply({
        templateDatePreset: "custom",
        templateDateFrom: toYyyyMmDd(fromD),
        templateDateTo: toYyyyMmDd(toD),
      });
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open && !firmLogsOpenField) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (firmLogsOpenField) {
          closeFirmLogsPicker();
        }
        else {
          closePicker();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeFirmLogsPicker, closePicker, firmLogsOpenField, open]);

  const renderPresetButton = (id: TemplateDatePreset, presetLabel: string) => {
    const active = applyMode === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => handlePresetInPopover(id)}
        className={`rounded-sm border border-solid px-2.5 py-1.5 text-xs font-medium transition-colors md:text-sm ${
          active
            ? isStaffMode
              ? "border-secondary bg-secondary/10 text-secondary"
              : "border-primary bg-primary/10 text-primary"
            : "border-gray-25 bg-gray-25 text-input-gray hover:border-gray-200"
        }`}
      >
        {presetLabel}
      </button>
    );
  };

  const portal =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            className="fixed inset-0 flex items-start justify-center overflow-y-auto py-6 px-3 sm:py-10 sm:px-4"
            style={{ zIndex: portalZIndex }}
            role="presentation"
          >
            <button
              type="button"
              aria-label="Close date picker"
              className="fixed inset-0 bg-black/45"
              onClick={closePicker}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="template-date-flights-title"
              className={dialogPanelClass}
              onClick={(e) => e.stopPropagation()}
            >
              <h4
                id="template-date-flights-title"
                className="mb-3 text-base font-semibold text-gray-font md:text-lg"
              >
                {modalHeading}
              </h4>
              <div
                className={`template-date-flights-picker w-full overflow-x-auto rounded-lg border border-solid border-gray-200 bg-gray-25/50 p-3 sm:p-4 ${colorClass}`}
              >
                <DayPicker
                  mode="range"
                  numberOfMonths={2}
                  locale={enUS}
                  month={month}
                  onMonthChange={setMonth}
                  selected={draftRange}
                  onSelect={(range) => {
                    setApplyMode("custom");
                    setDraftRange(range);
                  }}
                  captionLayout="dropdown-buttons"
                  fromYear={1999}
                  toYear={2100}
                  fixedWeeks
                  pagedNavigation
                  className="w-full"
                />
              </div>
              <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-2 sm:flex-1 sm:pr-2">
                  <div
                    className="flex flex-wrap gap-1.5"
                    role="group"
                    aria-label="Quick ranges"
                  >
                    {quickPresets.map(({ id, label: presetLabel }) =>
                      renderPresetButton(id, presetLabel),
                    )}
                  </div>
                  {showYearPresets ? (
                    <div
                      className="flex flex-wrap gap-1.5"
                      role="group"
                      aria-label="Year ranges"
                    >
                      {yearPresets.map(({ id, label: presetLabel }) =>
                        renderPresetButton(id, presetLabel),
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="flex shrink-0 justify-end gap-2">
                  <Button
                    text="Done"
                    color="secondary"
                    variant="filled"
                    size="fixed"
                    onClick={handleDone}
                  />
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  const firmLogsPortal =
    firmLogsOpenField && typeof document !== "undefined"
      ? createPortal(
          <div
            className="fixed inset-0 flex items-start justify-center overflow-y-auto py-6 px-3 sm:py-10 sm:px-4"
            style={{ zIndex: portalZIndex }}
            role="presentation"
          >
            <button
              type="button"
              aria-label="Close date picker"
              className="fixed inset-0 bg-black/45"
              onClick={closeFirmLogsPicker}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="firm-logs-date-picker-title"
              className="relative z-[1] mt-2 w-[calc(100vw-1.5rem)] max-w-md rounded-xl bg-white p-4 shadow-2xl sm:w-full sm:p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h4
                id="firm-logs-date-picker-title"
                className="mb-3 text-base font-semibold text-gray-font md:text-lg"
              >
                {firmLogsOpenField === "from" ? "From date" : "To date"}
              </h4>
              <div
                className={`template-date-flights-picker w-full overflow-x-auto rounded-lg border border-solid border-gray-200 bg-gray-25/50 p-3 sm:p-4 ${colorClass}`}
              >
                <DayPicker
                  mode="single"
                  locale={enUS}
                  month={month}
                  onMonthChange={setMonth}
                  selected={firmLogsSelectedDate}
                  onSelect={(date) => {
                    if (date) {
                      applyFirmLogsDate(firmLogsOpenField, date);
                    }
                  }}
                  disabled={firmLogsSingleDisabled}
                  captionLayout="dropdown-buttons"
                  fromYear={1999}
                  toYear={2100}
                  fixedWeeks
                  className="w-full"
                />
              </div>
              <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-4">
                <Button
                  text="Clear"
                  color="neutral"
                  variant="outlined"
                  size="fixed"
                  onClick={() => {
                    onApply(clearFirmLogsDateField(firmLogsOpenField, value));
                    closeFirmLogsPicker();
                  }}
                />
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  const renderFirmLogsDateField = (
    field: FirmLogsDateField,
    fieldLabel: string,
    displayValue: string,
  ) => (
    <div className="flex items-center gap-2">
      <span className="whitespace-nowrap text-sm font-semibold text-gray-font">
        {fieldLabel}
      </span>
      <button
        id={`template-date-flights-${field}-${variant}`}
        type="button"
        onClick={() => openFirmLogsPicker(field)}
        className={FIRM_LOGS_DATE_TRIGGER_CLASS}
      >
        <span
          className={`truncate font-normal ${
            displayValue ? "text-input-gray" : "text-gray-400"
          }`}
        >
          {displayValue || "[DD/MM/YYYY]"}
        </span>
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-font [&_svg]:h-full [&_svg]:w-full">
          <CalendarMonth />
        </span>
      </button>
    </div>
  );

  if (isFirmLogs) {
    return (
      <div className="flex flex-wrap items-center gap-4">
        {renderFirmLogsDateField("from", "From:", triggerLabels.from)}
        {renderFirmLogsDateField("to", "To:", triggerLabels.to)}
        {firmLogsPortal}
      </div>
    );
  }

  return (
    <div
      className={
        isCatalog
          ? "flex flex-col gap-2 md:gap-[.5rem] w-full"
          : "flex flex-col gap-2 md:gap-[.625rem]"
      }
    >
      {!hideLabel ? (
        <label
          htmlFor={`template-date-flights-${variant}`}
          className={labelFinalClass}
        >
          {isCatalog ? label ?? "By Date Created:" : surfaceLabel}
        </label>
      ) : null}
      {!isCatalog ? (
        <p className="text-xs text-gray-font md:text-sm">
          Choose a range on the calendar or a quick preset, then{" "}
          <span className="font-medium">Done</span> to apply
        </p>
      ) : null}
      <button
        id={`template-date-flights-${variant}`}
        type="button"
        onClick={openPicker}
        className={triggerButtonClass}
      >
        <span className={triggerIconRailClass}>
          <span className="flex h-5 w-5 items-center justify-center md:h-6 md:w-6">
            <CalendarMonth />
          </span>
        </span>
        <div className={insetGridClass}>
          <div className="flex min-w-0 flex-col justify-center px-2.5 py-1.5 sm:px-3 sm:py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-font md:text-xs">
              From
            </span>
            <span
              className={`truncate font-normal ${
                triggerLabels.from ? "text-input-gray" : "text-gray-400"
              }`}
            >
              {triggerLabels.from || "Select dates"}
            </span>
          </div>
          <div className="flex min-w-0 flex-col justify-center px-2.5 py-1.5 sm:px-3 sm:py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-font md:text-xs">
              To
            </span>
            <span
              className={`truncate font-normal ${
                triggerLabels.to ? "text-input-gray" : "text-gray-400"
              }`}
            >
              {triggerLabels.to || "Select dates"}
            </span>
          </div>
        </div>
      </button>
      {portal}
    </div>
  );
}
